/**
 * Componentes customizados do Chakra UI para o Sistema MEDICARE
 */

import { mode } from '@chakra-ui/theme-tools';

export const components = {
  // Button customizado
  Button: {
    baseStyle: {
      fontWeight: 'medium',
      borderRadius: 'md',
      transition: 'all 0.2s ease-in-out',
      _focus: {
        boxShadow: 'outline',
      },
      _disabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
      },
    },
    sizes: {
      xs: {
        h: '6',
        minW: '6',
        fontSize: 'xs',
        px: '2',
      },
      sm: {
        h: '8',
        minW: '8',
        fontSize: 'sm',
        px: '3',
      },
      md: {
        h: '10',
        minW: '10',
        fontSize: 'md',
        px: '4',
      },
      lg: {
        h: '12',
        minW: '12',
        fontSize: 'lg',
        px: '6',
      },
      xl: {
        h: '14',
        minW: '14',
        fontSize: 'xl',
        px: '8',
      },
    },
    variants: {
      solid: (props: any) => ({
        bg: mode('medicare.500', 'medicare.600')(props),
        color: 'white',
        _hover: {
          bg: mode('medicare.600', 'medicare.700')(props),
          transform: 'translateY(-1px)',
          boxShadow: 'lg',
        },
        _active: {
          bg: mode('medicare.700', 'medicare.800')(props),
          transform: 'translateY(0)',
        },
      }),
      outline: (props: any) => ({
        border: '2px solid',
        borderColor: mode('medicare.500', 'medicare.600')(props),
        color: mode('medicare.500', 'medicare.600')(props),
        _hover: {
          bg: mode('medicare.50', 'medicare.900')(props),
          borderColor: mode('medicare.600', 'medicare.700')(props),
          color: mode('medicare.600', 'medicare.700')(props),
        },
        _active: {
          bg: mode('medicare.100', 'medicare.800')(props),
        },
      }),
      ghost: (props: any) => ({
        color: mode('medicare.500', 'medicare.600')(props),
        _hover: {
          bg: mode('medicare.50', 'medicare.900')(props),
          color: mode('medicare.600', 'medicare.700')(props),
        },
        _active: {
          bg: mode('medicare.100', 'medicare.800')(props),
        },
      }),
      medical: (props: any) => ({
        bg: mode('medical.500', 'medical.600')(props),
        color: 'white',
        _hover: {
          bg: mode('medical.600', 'medical.700')(props),
          transform: 'translateY(-1px)',
          boxShadow: 'lg',
        },
        _active: {
          bg: mode('medical.700', 'medical.800')(props),
          transform: 'translateY(0)',
        },
      }),
      danger: (props: any) => ({
        bg: mode('red.500', 'red.600')(props),
        color: 'white',
        _hover: {
          bg: mode('red.600', 'red.700')(props),
          transform: 'translateY(-1px)',
          boxShadow: 'lg',
        },
        _active: {
          bg: mode('red.700', 'red.800')(props),
          transform: 'translateY(0)',
        },
      }),
    },
    defaultProps: {
      variant: 'solid',
      size: 'md',
    },
  },

  // Input customizado
  Input: {
    baseStyle: {
      field: {
        borderRadius: 'md',
        transition: 'all 0.2s ease-in-out',
        _focus: {
          borderColor: 'medicare.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-medicare-500)',
        },
        _invalid: {
          borderColor: 'red.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-red-500)',
        },
      },
    },
    sizes: {
      sm: {
        field: {
          fontSize: 'sm',
          px: '3',
          h: '8',
        },
      },
      md: {
        field: {
          fontSize: 'md',
          px: '4',
          h: '10',
        },
      },
      lg: {
        field: {
          fontSize: 'lg',
          px: '4',
          h: '12',
        },
      },
    },
    variants: {
      outline: (props: any) => ({
        field: {
          bg: mode('white', 'gray.800')(props),
          border: '2px solid',
          borderColor: mode('gray.200', 'gray.600')(props),
          _hover: {
            borderColor: mode('gray.300', 'gray.500')(props),
          },
          _focus: {
            borderColor: 'medicare.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-medicare-500)',
          },
        },
      }),
      filled: (props: any) => ({
        field: {
          bg: mode('gray.100', 'gray.700')(props),
          border: '2px solid transparent',
          _hover: {
            bg: mode('gray.200', 'gray.600')(props),
          },
          _focus: {
            bg: mode('white', 'gray.800')(props),
            borderColor: 'medicare.500',
          },
        },
      }),
    },
    defaultProps: {
      variant: 'outline',
      size: 'md',
    },
  },

  // Card customizado
  Card: {
    baseStyle: (props: any) => ({
      container: {
        bg: mode('white', 'gray.800')(props),
        borderRadius: 'lg',
        boxShadow: 'card',
        border: '1px solid',
        borderColor: mode('gray.200', 'gray.700')(props),
        transition: 'all 0.2s ease-in-out',
        _hover: {
          boxShadow: 'cardHover',
          transform: 'translateY(-2px)',
        },
      },
      header: {
        p: '6',
        pb: '0',
      },
      body: {
        p: '6',
      },
      footer: {
        p: '6',
        pt: '0',
      },
    }),
    variants: {
      elevated: (props: any) => ({
        container: {
          bg: mode('white', 'gray.800')(props),
          boxShadow: 'lg',
          border: 'none',
        },
      }),
      outline: (props: any) => ({
        container: {
          bg: 'transparent',
          border: '2px solid',
          borderColor: mode('gray.200', 'gray.700')(props),
          boxShadow: 'none',
        },
      }),
      medical: (props: any) => ({
        container: {
          bg: mode('medical.50', 'medical.900')(props),
          borderColor: mode('medical.200', 'medical.700')(props),
        },
      }),
    },
    defaultProps: {
      variant: 'elevated',
    },
  },

  // Badge customizado
  Badge: {
    baseStyle: {
      px: '2',
      py: '1',
      borderRadius: 'md',
      fontSize: 'xs',
      fontWeight: 'medium',
      textTransform: 'uppercase',
      letterSpacing: 'wide',
    },
    variants: {
      solid: (props: any) => ({
        bg: mode('medicare.500', 'medicare.600')(props),
        color: 'white',
      }),
      subtle: (props: any) => ({
        bg: mode('medicare.100', 'medicare.800')(props),
        color: mode('medicare.800', 'medicare.100')(props),
      }),
      outline: (props: any) => ({
        border: '1px solid',
        borderColor: mode('medicare.500', 'medicare.600')(props),
        color: mode('medicare.500', 'medicare.600')(props),
      }),
      status: {
        agendada: {
          bg: 'blue.100',
          color: 'blue.800',
        },
        em_andamento: {
          bg: 'yellow.100',
          color: 'yellow.800',
        },
        concluida: {
          bg: 'green.100',
          color: 'green.800',
        },
        cancelada: {
          bg: 'red.100',
          color: 'red.800',
        },
      },
    },
    defaultProps: {
      variant: 'subtle',
    },
  },

  // Alert customizado
  Alert: {
    baseStyle: {
      container: {
        borderRadius: 'md',
        p: '4',
      },
      title: {
        fontWeight: 'semibold',
        mb: '1',
      },
      description: {
        fontSize: 'sm',
      },
      icon: {
        mr: '3',
      },
    },
    variants: {
      subtle: (props: any) => {
        const { colorScheme: c } = props;
        return {
          container: {
            bg: mode(`${c}.50`, `${c}.900`)(props),
            border: '1px solid',
            borderColor: mode(`${c}.200`, `${c}.700`)(props),
          },
          icon: {
            color: mode(`${c}.500`, `${c}.300`)(props),
          },
          title: {
            color: mode(`${c}.800`, `${c}.100`)(props),
          },
          description: {
            color: mode(`${c}.700`, `${c}.200`)(props),
          },
        };
      },
      solid: (props: any) => {
        const { colorScheme: c } = props;
        return {
          container: {
            bg: mode(`${c}.500`, `${c}.600`)(props),
            color: 'white',
          },
        };
      },
    },
    defaultProps: {
      variant: 'subtle',
      colorScheme: 'medicare',
    },
  },

  // Modal customizado
  Modal: {
    baseStyle: (props: any) => ({
      dialog: {
        bg: mode('white', 'gray.800')(props),
        borderRadius: 'xl',
        boxShadow: 'modal',
        mx: '4',
      },
      header: {
        fontSize: 'xl',
        fontWeight: 'semibold',
        pb: '4',
        borderBottom: '1px solid',
        borderColor: mode('gray.200', 'gray.700')(props),
      },
      body: {
        py: '6',
      },
      footer: {
        pt: '4',
        borderTop: '1px solid',
        borderColor: mode('gray.200', 'gray.700')(props),
      },
      overlay: {
        bg: 'blackAlpha.600',
        backdropFilter: 'blur(4px)',
      },
    }),
  },

  // Tooltip customizado
  Tooltip: {
    baseStyle: (props: any) => ({
      bg: mode('gray.700', 'gray.300')(props),
      color: mode('white', 'gray.800')(props),
      fontSize: 'sm',
      px: '2',
      py: '1',
      borderRadius: 'md',
      fontWeight: 'medium',
      maxW: 'xs',
      boxShadow: 'lg',
    }),
  },

  // Menu customizado
  Menu: {
    baseStyle: (props: any) => ({
      list: {
        bg: mode('white', 'gray.800')(props),
        borderRadius: 'md',
        border: '1px solid',
        borderColor: mode('gray.200', 'gray.700')(props),
        boxShadow: 'lg',
        py: '2',
        minW: '200px',
      },
      item: {
        px: '3',
        py: '2',
        fontSize: 'sm',
        transition: 'all 0.2s ease-in-out',
        _hover: {
          bg: mode('medicare.50', 'medicare.900')(props),
          color: mode('medicare.600', 'medicare.300')(props),
        },
        _focus: {
          bg: mode('medicare.50', 'medicare.900')(props),
          color: mode('medicare.600', 'medicare.300')(props),
        },
      },
    }),
  },

  // Tabs customizado
  Tabs: {
    variants: {
      line: (props: any) => ({
        tab: {
          borderBottom: '2px solid transparent',
          color: mode('gray.600', 'gray.400')(props),
          _selected: {
            color: mode('medicare.600', 'medicare.300')(props),
            borderColor: mode('medicare.600', 'medicare.300')(props),
          },
          _hover: {
            color: mode('medicare.500', 'medicare.400')(props),
          },
        },
        tabpanel: {
          pt: '4',
        },
      }),
      enclosed: (props: any) => ({
        tab: {
          border: '1px solid',
          borderColor: mode('gray.200', 'gray.700')(props),
          bg: mode('gray.50', 'gray.800')(props),
          _selected: {
            bg: mode('white', 'gray.700')(props),
            color: mode('medicare.600', 'medicare.300')(props),
            borderColor: mode('gray.200', 'gray.600')(props),
            borderBottomColor: mode('white', 'gray.700')(props),
          },
        },
        tabpanel: {
          border: '1px solid',
          borderColor: mode('gray.200', 'gray.700')(props),
          borderTop: 'none',
          bg: mode('white', 'gray.700')(props),
          p: '4',
        },
      }),
    },
    defaultProps: {
      variant: 'line',
      colorScheme: 'medicare',
    },
  },
};